import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { Check, X, AlertTriangle, Loader2, Zap, Copy } from 'lucide-react'
import { JourneyVisualization, StepDetailDrawer } from '@features/dashboards/testing-ground/components/JourneyVisualization'
import { CopyButton, RawJsonCollapsible, ComboSelect, inputCls, labelCls, SectionLabel } from '../styles'
import { KYC_SCENARIOS, BLANK_FORM } from '@features/dashboards/testing-ground/data'
import { proxyUrl, buildHeaders, buildIndividual, scenarioToForm, outcomeColors } from '@features/dashboards/testing-ground/utils'
import type { Creds, IndividualForm, LogEntry } from '@features/dashboards/testing-ground/types'
import { deriveCascadeWaterfall, extractDuplicateCheck, hasDuplicateMatch, outcomeLabel } from '../utils'
import { ConfiguringChecklist } from './ConfiguringChecklist'
import { InteractionDiagram } from './InteractionDiagram'
import { CascadeConfigPanel } from './CascadeConfigPanel'
import { BuilderLockedBanner } from './BuilderLockedBanner'
import { RiskProfileConfigPanel } from './RiskProfileConfigPanel'
import { DuplicatesConfigPanel } from './DuplicatesConfigPanel'
import { KycGuidedView } from './KycGuidedView'
import { ExperienceModeSwitcher } from './ExperienceModeSwitcher'
import type { ExperienceMode } from './ExperienceModeSwitcher'
import { ExistingCustomerPanel } from './ExistingCustomerPanel'
import type { ExistingCustomerSelection } from './ExistingCustomerPanel'
import type { CustomerMode } from './NewExistingToggle'
import { StageSaveBar } from './StageSaveBar'
import { CONFIG_CHECKLISTS, DEFAULT_CASCADE_CHECKS, DEFAULT_DUPLICATE_RULES } from '../data'
import { buildDuplicatesDiagramDsl, buildKycDiagramDsl } from '../dynamicInteractionDiagrams'
import type { WorkflowDerivedConfig } from '../workflowDerivedConfig'
import type { PlatformJourneyRecord } from '@api/client'
import type { AnyObj, CascadeCheckConfig, DuplicateRuleConfig } from '../types'
import type { AiConfigureSuggestions, AiIdSuggestion, AiStageSuggestionState } from '../aiConfigure'
import { AiSuggestionBanner } from './AiSuggestionBanner'

function resultDotColor(r: 'PASS' | 'FAIL' | 'REVIEW') {
  if (r === 'PASS') return '#15803d'
  if (r === 'FAIL') return '#b91c1c'
  return '#c2410c'
}

// Duplicate matching isn't its own product — it runs as part of the same KYC
// workflow execution, so its result is just another card in the KYC result panel.
function DuplicateCheckCard({ matched }: { matched: boolean }) {
  return (
    <div
      className="rounded-[14px] overflow-hidden p-4 space-y-2"
      style={{
        border: matched ? '1px solid rgba(251,146,60,.3)' : '1px solid rgba(52,211,153,.3)',
        background: matched ? 'rgba(251,146,60,.04)' : 'rgba(52,211,153,.04)',
      }}
    >
      <div className="flex items-center gap-2">
        <Copy size={13} className={matched ? 'text-amber-700' : 'text-emerald-700'} />
        <p className={`text-[10px] font-semibold uppercase tracking-wider ${matched ? 'text-amber-700' : 'text-emerald-700'}`}>Duplicate Check</p>
      </div>
      <p className="text-[12px] text-[#1e293b]">
        {matched ? 'A possible duplicate entity was found — worth a closer look.' : 'No duplicate entity was found.'}
      </p>
    </div>
  )
}

interface KycCascadeStageProps {
  creds: Creds
  hasCreds: boolean
  addLog: (e: Omit<LogEntry, 'id' | 'ts'>) => void
  entityId: string | null
  onEntityCreated: (entityId: string) => void
  workflowResult: AnyObj | null
  onResult: (workflowResult: AnyObj | null, fullResult: unknown) => void
  onRunningChange: (running: boolean) => void
  customerMode: CustomerMode
  existingCustomer: ExistingCustomerSelection | null
  customerName: string | null
  platformVersion: 'v1' | 'v2'
  /** The workflow selected/opened in the Workflow Builder slate, if any — used to
   * confirm rather than re-ask for cascade config once it's already been decided. */
  builderConfig: WorkflowDerivedConfig | null
  experienceMode: ExperienceMode
  onExperienceModeChange: (mode: ExperienceMode) => void
  journeyId: number | null
  journeyRecord: PlatformJourneyRecord | null
  onJourneySaved: () => void
  aiSuggestions?: AiConfigureSuggestions | null
  /** Lifted to the tool shell so Account Setup's checklist and this stage's
   * own banners share one apply/dismiss state per suggestion. */
  cascadeAi: AiStageSuggestionState<AiIdSuggestion[]>
  duplicatesAi: AiStageSuggestionState<DuplicateRuleConfig[]>
  riskAi: AiStageSuggestionState<AiIdSuggestion[]>
}

export function KycCascadeStage({
  creds, hasCreds, addLog, entityId, onEntityCreated, workflowResult, onResult, onRunningChange,
  customerMode, existingCustomer, customerName, platformVersion, builderConfig,
  experienceMode, onExperienceModeChange, journeyId, journeyRecord, onJourneySaved,
  aiSuggestions, cascadeAi, duplicatesAi, riskAi,
}: KycCascadeStageProps) {
  const [workflows, setWorkflows] = useState<{ serviceName: string; workflowName: string }[]>([])
  const [selectedWorkflow, setSelectedWorkflow] = useState('')
  const lockedByBuilder = !!builderConfig && builderConfig.kycSteps.length > 0
  const builderWorkflowMatch = lockedByBuilder
    ? workflows.find((w) => w.workflowName === builderConfig!.workflowName)
    : undefined
  const [cascadeChecks, setCascadeChecks] = useState<CascadeCheckConfig[]>(DEFAULT_CASCADE_CHECKS)
  const [customDuplicateRules, setCustomDuplicateRules] = useState<DuplicateRuleConfig[]>([])
  const [checklistEnabled, setChecklistEnabled] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(CONFIG_CHECKLISTS.kyc.map((i) => [i.id, true]))
  )
  function toggleChecklistItem(id: string) {
    setChecklistEnabled((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  useEffect(() => {
    if (!journeyRecord) return
    if (journeyRecord.kycConfig) {
      const cfg = journeyRecord.kycConfig as {
        selectedWorkflow?: string; cascadeChecks?: CascadeCheckConfig[]; customDuplicateRules?: DuplicateRuleConfig[]
      }
      if (cfg.selectedWorkflow) setSelectedWorkflow(cfg.selectedWorkflow)
      if (cfg.cascadeChecks) setCascadeChecks(cfg.cascadeChecks)
      if (cfg.customDuplicateRules) setCustomDuplicateRules(cfg.customDuplicateRules)
    }
    if (journeyRecord.kycChecklist) setChecklistEnabled(journeyRecord.kycChecklist as Record<string, boolean>)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyRecord?.id])

  const [scenarioIdx, setScenarioIdx] = useState<number | null>(null)
  const [form, setForm] = useState<IndividualForm>(BLANK_FORM)
  const [loading, setLoading] = useState(false)
  const [pollAttempt, setPollAttempt] = useState(0)
  const [selectedStep, setSelectedStep] = useState<string | null>(null)
  const [fullResult, setFullResult] = useState<unknown>(null)

  const doFetch = useCallback(async (method: string, path: string, body: unknown | undefined, annotation: string) => {
    const url = `${creds.apiBase}${path}`
    const start = Date.now()
    const res = await fetch(proxyUrl(path), {
      method,
      headers: buildHeaders(creds, method !== 'GET'),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })
    const durationMs = Date.now() - start
    let data: unknown
    try { data = await res.json() } catch { data = {} }
    addLog({ method, url, status: res.status, durationMs, requestBody: body, responseBody: data, annotation })
    if (!res.ok) throw new Error((data as { error?: string })?.error ?? res.statusText)
    return data
  }, [creds, addLog])

  const loadWorkflows = useCallback(async () => {
    try {
      const data = await doFetch('GET', '/workflows', undefined, 'List configured KYC workflows') as { workflows?: { serviceName: string; workflowName: string }[] }
      const wfs = data?.workflows ?? []
      setWorkflows(wfs)
      if (wfs.length > 0) setSelectedWorkflow(wfs[0].workflowName)
    } catch { /* logged */ }
  }, [doFetch])

  const lastAutoLoadedCustomer = useRef('')
  useEffect(() => {
    if (hasCreds && creds.customerId !== lastAutoLoadedCustomer.current) {
      lastAutoLoadedCustomer.current = creds.customerId
      void loadWorkflows()
    }
  }, [hasCreds, creds.customerId, loadWorkflows])

  // Once the workflow picked in Workflow Builder is confirmed to exist in this
  // customer's live workflow list, use it instead of whatever loadWorkflows()
  // defaulted to — that's the whole point of not re-asking here.
  useEffect(() => {
    if (builderWorkflowMatch) setSelectedWorkflow(builderWorkflowMatch.workflowName)
  }, [builderWorkflowMatch])

  function selectPersona(idx: number) {
    setScenarioIdx(idx)
    setForm(scenarioToForm(KYC_SCENARIOS[idx]))
  }

  async function pollResult(eid: string, wfeId: string, wf: string) {
    for (let i = 0; i < 20; i++) {
      setPollAttempt(i + 1)
      try {
        const data = await doFetch(
          'GET',
          `/individuals/${eid}/serviceprofiles/DEFAULT/workflows/${wf}/executions/${wfeId}`,
          undefined,
          `Poll KYC result (attempt ${i + 1}/20)`,
        ) as { workflowResult?: AnyObj }
        const state = data?.workflowResult?.workflowExecutionState
        if (state === 'COMPLETED' || state === 'ERROR' || state === 'TIMEOUT') {
          onResult(data.workflowResult ?? null, data)
          setFullResult(data)
          return
        }
      } catch { /* keep polling */ }
      await new Promise(r => setTimeout(r, 4000))
    }
    setPollAttempt(0)
  }

  async function runKycWorkflow() {
    if (!selectedWorkflow) return
    const wf = selectedWorkflow
    setLoading(true)
    onRunningChange(true)
    onResult(null, null)
    setFullResult(null)
    setSelectedStep(null)
    setPollAttempt(0)
    try {
      if (entityId) {
        // IDV already created this entity — execute the workflow on it directly.
        const data = await doFetch('POST', `/individuals/${entityId}/serviceprofiles/DEFAULT/workflows/${wf}/execute`, { workflowName: wf }, 'Execute KYC workflow on the entity created during IDV') as {
          workflowResult?: AnyObj & { workflowExecutionState?: string; workflowExecutionId?: string }
        }
        const wr = data?.workflowResult
        const state = wr?.workflowExecutionState
        if (state === 'COMPLETED' || state === 'ERROR' || state === 'TIMEOUT') {
          onResult(wr ?? null, data); setFullResult(data)
        } else if (wr?.workflowExecutionId) {
          await pollResult(entityId, wr.workflowExecutionId, wf)
        }
      } else {
        const payload = { workflowName: wf, ...buildIndividual(form) }
        const data = await doFetch('POST', `/individuals/new/serviceprofiles/DEFAULT/workflows/${wf}/execute`, payload, 'Create individual + execute KYC workflow in one call') as {
          individual?: { entityId?: string }
          workflowResult?: AnyObj & { workflowExecutionState?: string; workflowExecutionId?: string }
        }
        const eid = data?.individual?.entityId
        if (eid) onEntityCreated(eid)
        const wr = data?.workflowResult
        const state = wr?.workflowExecutionState
        if (state === 'COMPLETED' || state === 'ERROR' || state === 'TIMEOUT') {
          onResult(wr ?? null, data); setFullResult(data)
        } else if (wr?.workflowExecutionId && eid) {
          await pollResult(eid, wr.workflowExecutionId, wf)
        } else {
          onResult(wr ?? null, data); setFullResult(data)
        }
      }
    } catch { /* logged */ } finally {
      setLoading(false)
      onRunningChange(false)
      setPollAttempt(0)
    }
  }

  const outcome = workflowResult ? outcomeLabel(workflowResult) : ''
  const oc = outcomeColors(outcome)
  const steps = workflowResult?.steps ?? {}
  const stepResults: AnyObj[] = workflowResult?.workflowStepResults ?? []
  const wfeId = (workflowResult?.workflowExecutionId as string | undefined) ?? null
  const cascade = deriveCascadeWaterfall(workflowResult)
  const diagramDsl = useMemo(
    () => buildKycDiagramDsl({ platformVersion, workflowName: selectedWorkflow, checks: cascadeChecks }),
    [platformVersion, selectedWorkflow, cascadeChecks],
  )

  // Duplicate matching runs as part of the same workflow execution, not as a
  // separate product — so it's configured and surfaced here rather than as its
  // own stage.
  const duplicateCheck = extractDuplicateCheck(workflowResult)
  const duplicateMatched = hasDuplicateMatch(duplicateCheck)
  const duplicatesDiagramDsl = useMemo(
    () => buildDuplicatesDiagramDsl({ platformVersion, defaultRules: DEFAULT_DUPLICATE_RULES, customRules: customDuplicateRules }),
    [platformVersion, customDuplicateRules],
  )
  const existingDuplicateRules = existingCustomer?.scopeData?.rulesConfig.customDuplicateRuleSet ?? []
  const existingDuplicateMode = existingCustomer?.scopeData?.rulesConfig.duplicateRules

  const workflowFieldNode = (
    <div>
      <label className={labelCls}>Workflow</label>
      {lockedByBuilder && (
        <div className="mb-2">
          <BuilderLockedBanner>
            Using <span className="font-semibold">{builderConfig!.workflowName}</span> — already configured in Workflow Builder.
            {!builderWorkflowMatch && ' No live workflow with that exact name was found for this customer, so pick one manually below.'}
          </BuilderLockedBanner>
        </div>
      )}
      <ComboSelect
        value={selectedWorkflow}
        onChange={setSelectedWorkflow}
        options={workflows.map(w => ({ value: w.workflowName }))}
        placeholder="Workflow name"
        className={inputCls}
        disabled={!!builderWorkflowMatch}
      />
    </div>
  )

  const aiBannersNode = (
    <>
      {cascadeAi.visible && aiSuggestions?.cascade && (
        <AiSuggestionBanner
          rationale={aiSuggestions.cascade.rationale}
          confidence={aiSuggestions.cascade.confidence}
          applied={cascadeAi.applied}
          onApply={cascadeAi.apply}
          onDismiss={cascadeAi.dismiss}
        />
      )}
      {riskAi.visible && aiSuggestions?.risk && (
        <AiSuggestionBanner
          rationale={aiSuggestions.risk.rationale}
          confidence={aiSuggestions.risk.confidence}
          applied={riskAi.applied}
          onApply={riskAi.apply}
          onDismiss={riskAi.dismiss}
        />
      )}
      {duplicatesAi.visible && aiSuggestions?.duplicates && (
        <AiSuggestionBanner
          rationale={aiSuggestions.duplicates.rationale}
          confidence={aiSuggestions.duplicates.confidence}
          applied={duplicatesAi.applied}
          onApply={duplicatesAi.apply}
          onDismiss={duplicatesAi.dismiss}
        />
      )}
    </>
  )

  const technicalDetailsNode = (
    <>
      {workflowFieldNode}
      {aiBannersNode}
      <CascadeConfigPanel onChecksChange={setCascadeChecks} derivedSteps={lockedByBuilder ? builderConfig!.kycSteps : null} aiSeed={cascadeAi.seed} />
      <RiskProfileConfigPanel aiSeed={riskAi.seed} />
      <DuplicatesConfigPanel onRulesChange={({ customRules }) => setCustomDuplicateRules(customRules)} aiSeed={duplicatesAi.seed} />
      <InteractionDiagram stageId="kyc" dsl={diagramDsl} />
      <InteractionDiagram stageId="duplicates" dsl={duplicatesDiagramDsl} />
      {workflowResult && (
        <>
          <div className="p-4 rounded-[14px]" style={{ background: '#ffffff', border: '1px solid #e5e7eb' }}>
            <JourneyVisualization
              view="flowchart"
              steps={steps}
              workflowStepResults={stepResults}
              isRunning={false}
              selectedStep={selectedStep}
              onStepClick={setSelectedStep}
              entityId={entityId}
              workflowName={selectedWorkflow}
              workflowExecutionId={wfeId}
              overallStatus={outcome}
            />
          </div>
          {selectedStep && <StepDetailDrawer selectedStep={selectedStep} workflowStepResults={stepResults} />}
          {cascade.length > 0 && (
            <div className="rounded-[11px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
              <div className="px-4 py-2.5" style={{ borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">Verification Cascade — sources consulted per step</p>
              </div>
              <div className="divide-y" style={{ borderColor: '#e5e7eb' }}>
                {cascade.map((hit, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-2">
                    <span className="text-[10px] font-mono text-[#64748b] w-24 shrink-0">{hit.stepName}</span>
                    <span className="text-[11px] text-[#475569] flex-1 font-mono truncate">{hit.objectType}</span>
                    <span className="text-[10px] font-mono text-[#64748b] truncate max-w-[160px]">{hit.source}</span>
                    <span
                      className="text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0"
                      style={{
                        background: hit.result === 'MATCH' || hit.result === 'CLEAR' || hit.result === 'PASS' ? 'rgba(52,211,153,.15)' : hit.result === 'FAIL' || hit.result === 'NO_MATCH' ? 'rgba(248,113,113,.15)' : 'rgba(148,163,184,.15)',
                        color: hit.result === 'MATCH' || hit.result === 'CLEAR' || hit.result === 'PASS' ? '#15803d' : hit.result === 'FAIL' || hit.result === 'NO_MATCH' ? '#b91c1c' : '#475569',
                      }}
                    >
                      {hit.result}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {duplicateMatched !== null && <DuplicateCheckCard matched={duplicateMatched} />}
          <RawJsonCollapsible data={fullResult} />
        </>
      )}
    </>
  )

  return (
    <div className="space-y-3">
      {customerMode === 'existing' ? (
        <div className="space-y-3">
          <ExistingCustomerPanel
            productKey="kyc"
            sections={['KYC', 'Recipes/Workflows']}
            title="KYC + Cascade — Existing Customer"
            description="Shows this customer's KYC ruleset and cascade/workflow settings as scoped in DealHub. Reads DealHub's own presales record, not FrankieOne's live platform."
            sharedSelection={existingCustomer}
            searchable={false}
          />
          <ExistingCustomerPanel
            productKey="duplicates"
            sections={[]}
            title="Duplicate Detection — Existing Customer"
            description="Shows this customer's Duplicate Detection settings as scoped in DealHub (default vs custom match rules). Reads DealHub's own presales record, not FrankieOne's live platform."
            showCascadeSummary={false}
            sharedSelection={existingCustomer}
            searchable={false}
          />
          {existingDuplicateMode === 'custom' && (
            <div className="rounded-[14px] p-4 space-y-2" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">Custom duplicate rules</p>
              {existingDuplicateRules.length === 0 ? (
                <p className="text-[11px] text-[#475569]">Custom rules were selected but none have been recorded in Scope yet.</p>
              ) : (
                <div className="space-y-1.5">
                  {existingDuplicateRules.map((rule, i) => (
                    <div key={i} className="px-3 py-2 rounded-[9px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] font-semibold text-[#1e293b]">{rule.name || 'Unnamed rule'}</span>
                        <span className="text-[9px] font-mono text-[#64748b]">{rule.method} · {rule.strength}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {rule.matchFields.map((f) => (
                          <span key={f} className="text-[9px] px-1.5 py-0.5 rounded bg-[#e5e7eb] text-[#64748b] font-mono">{f}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {aiBannersNode}
          <CascadeConfigPanel onChecksChange={setCascadeChecks} aiSeed={cascadeAi.seed} />
          <RiskProfileConfigPanel aiSeed={riskAi.seed} />
          <DuplicatesConfigPanel onRulesChange={({ customRules }) => setCustomDuplicateRules(customRules)} aiSeed={duplicatesAi.seed} />
        </div>
      ) : (
      <>
      <ExperienceModeSwitcher mode={experienceMode} onChange={onExperienceModeChange} />
      <StageSaveBar
        journeyId={journeyId}
        buildFields={() => ({ kycConfig: { selectedWorkflow, cascadeChecks, customDuplicateRules }, kycChecklist: checklistEnabled })}
        onSaved={onJourneySaved}
      />

      {experienceMode === 'guided' && (
        <KycGuidedView
          hasCreds={hasCreds}
          entityId={entityId}
          loading={loading}
          pollAttempt={pollAttempt}
          scenarioIdx={scenarioIdx}
          onSelectPersona={selectPersona}
          onRun={() => void runKycWorkflow()}
          canRun={!loading && hasCreds && !!selectedWorkflow && (!!entityId || scenarioIdx !== null)}
          workflowResult={workflowResult}
          outcome={outcome}
          customerName={customerName}
          checklistItems={CONFIG_CHECKLISTS.kyc}
          checklistEnabled={checklistEnabled}
          onToggleChecklistItem={toggleChecklistItem}
          technicalDetails={technicalDetailsNode}
        />
      )}

      {experienceMode === 'classic' && (
      <>
      <ConfiguringChecklist productLabel="KYC + Cascade" items={CONFIG_CHECKLISTS.kyc} enabled={checklistEnabled} onToggle={toggleChecklistItem} customerName={customerName} />
      {aiBannersNode}
      <CascadeConfigPanel onChecksChange={setCascadeChecks} derivedSteps={lockedByBuilder ? builderConfig!.kycSteps : null} aiSeed={cascadeAi.seed} />
      <RiskProfileConfigPanel aiSeed={riskAi.seed} />
      <DuplicatesConfigPanel onRulesChange={({ customRules }) => setCustomDuplicateRules(customRules)} aiSeed={duplicatesAi.seed} />
      <InteractionDiagram stageId="kyc" dsl={diagramDsl} />
      <InteractionDiagram stageId="duplicates" dsl={duplicatesDiagramDsl} />

      <div className="grid gap-4" style={{ gridTemplateColumns: 'minmax(320px, 380px) 1fr' }}>
        {/* Form panel */}
        <div className="rounded-[14px] overflow-hidden" style={{ background: '#ffffff', border: '1px solid #e5e7eb' }}>
          <div className="px-4 py-3" style={{ background: '#ffffff', borderBottom: '1px solid #e5e7eb' }}>
            <p className="text-[12px] font-semibold text-[#1e293b]">KYC + Cascade</p>
            <p className="text-[10px] text-[#64748b] mt-0.5">
              {entityId ? 'Runs on the entity created during IDV' : 'Creates a new test entity and runs the workflow'}
            </p>
          </div>
          <div className="p-4 space-y-3">
            {workflowFieldNode}

            {!entityId && (
              <div>
                <SectionLabel>Test Persona</SectionLabel>
                <div className="flex flex-wrap gap-1.5">
                  {KYC_SCENARIOS.map((s, i) => {
                    const isActive = scenarioIdx === i
                    return (
                      <button
                        key={i}
                        onClick={() => selectPersona(i)}
                        className="flex items-center gap-1.5 px-2.5 py-[5px] rounded-[8px] text-[11px] font-medium transition-all"
                        style={isActive
                          ? { background: 'rgba(99,102,241,.16)', border: '1px solid rgba(99,102,241,.45)', color: '#1e293b' }
                          : { background: '#f8fafc', border: '1px solid #e5e7eb', color: '#475569' }
                        }
                      >
                        <span className="w-[6px] h-[6px] rounded-full shrink-0" style={{ background: resultDotColor(s.expectedResult) }} />
                        {s.label.split('—').slice(1).join('—').trim() || s.label}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            <button
              onClick={() => void runKycWorkflow()}
              disabled={loading || !hasCreds || !selectedWorkflow || (!entityId && scenarioIdx === null)}
              className="w-full py-2.5 rounded-[9px] text-[13px] font-semibold text-white transition-all disabled:opacity-40"
              style={{ background: '#6366f1', boxShadow: '0 3px 12px rgba(99,102,241,.45)' }}
            >
              {loading
                ? <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" />Running…</span>
                : 'Run KYC Workflow'}
            </button>
            {!hasCreds && <p className="text-[10px] text-amber-700 text-center">Configure credentials above to make live calls</p>}
            {!entityId && scenarioIdx === null && hasCreds && (
              <p className="text-[10px] text-amber-700 text-center">Pick a test persona first</p>
            )}

            {entityId && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-[8px]" style={{ background: 'rgba(52,211,153,.06)', border: '1px solid rgba(52,211,153,.2)' }}>
                <span className="text-[10px] font-semibold text-emerald-700">entityId</span>
                <span className="text-[10px] font-mono text-[#475569] flex-1 truncate">{entityId}</span>
                <CopyButton text={entityId} />
              </div>
            )}
          </div>
        </div>

        {/* Result panel */}
        <div className="space-y-3 min-w-0">
          {loading && (
            <div className="p-5 rounded-[14px] space-y-4" style={{ background: '#ffffff', border: '1px solid #e5e7eb' }}>
              <div className="flex items-center gap-3">
                <Loader2 size={18} className="animate-spin text-amber-700 shrink-0" />
                <p className="text-[13px] font-semibold text-[#b45309]">
                  {pollAttempt === 0 ? 'Waiting for response…' : `Polling for result… attempt ${pollAttempt}/20`}
                </p>
              </div>
              {pollAttempt > 0 && (
                <div className="h-[5px] rounded-full" style={{ background: '#f1f5f9' }}>
                  <div className="h-full rounded-full transition-all duration-300" style={{ width: `${Math.min((pollAttempt / 20) * 100, 100)}%`, background: 'linear-gradient(90deg, #b45309, #b45309)' }} />
                </div>
              )}
            </div>
          )}

          {!loading && !workflowResult && (
            <div className="flex flex-col items-center justify-center py-10 rounded-[14px]" style={{ border: '2px dashed #e5e7eb', background: '#f8fafc' }}>
              <div className="w-12 h-12 rounded-[12px] flex items-center justify-center mb-4" style={{ background: 'rgba(99,102,241,.12)', border: '1px solid rgba(99,102,241,.2)' }}>
                <Zap size={22} className="text-[#6366f1]" />
              </div>
              <p className="text-[14px] font-semibold text-[#64748b]">Run the workflow to see the journey</p>
            </div>
          )}

          {!loading && workflowResult && (
            <div className="space-y-3" style={{ animation: 'fadeUp .25s ease' }}>
              <div className="p-4 rounded-[14px]" style={{ background: oc.bg, border: `1px solid ${oc.border}` }}>
                <div className="flex items-start gap-4">
                  <div className="w-[46px] h-[46px] rounded-[12px] flex items-center justify-center shrink-0" style={{ background: `${oc.icon}20`, border: `1px solid ${oc.border}` }}>
                    {outcome === 'PASS' && <Check size={22} style={{ color: oc.icon }} />}
                    {outcome === 'FAIL' && <X size={22} style={{ color: oc.icon }} />}
                    {outcome === 'REFER' && <AlertTriangle size={22} style={{ color: oc.icon }} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-[19px] font-extrabold" style={{ color: oc.text }}>{outcome}</span>
                    {workflowResult.riskAssessment && (
                      <span className="ml-3 text-[10px] font-bold px-2 py-0.5 rounded-[6px]" style={{ background: `${oc.icon}20`, color: oc.text }}>
                        RISK {workflowResult.riskAssessment.riskLevel} · {workflowResult.riskAssessment.riskScore ?? '—'}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-4 rounded-[14px]" style={{ background: '#ffffff', border: '1px solid #e5e7eb' }}>
                <JourneyVisualization
                  view="flowchart"
                  steps={steps}
                  workflowStepResults={stepResults}
                  isRunning={false}
                  selectedStep={selectedStep}
                  onStepClick={setSelectedStep}
                  entityId={entityId}
                  workflowName={selectedWorkflow}
                  workflowExecutionId={wfeId}
                  overallStatus={outcome}
                />
              </div>

              {selectedStep && <StepDetailDrawer selectedStep={selectedStep} workflowStepResults={stepResults} />}

              {/* Cascade waterfall — every source/provider each step consulted */}
              {cascade.length > 0 && (
                <div className="rounded-[11px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
                  <div className="px-4 py-2.5" style={{ borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">Verification Cascade — sources consulted per step</p>
                  </div>
                  <div className="divide-y" style={{ borderColor: '#e5e7eb' }}>
                    {cascade.map((hit, i) => (
                      <div key={i} className="flex items-center gap-3 px-4 py-2">
                        <span className="text-[10px] font-mono text-[#64748b] w-24 shrink-0">{hit.stepName}</span>
                        <span className="text-[11px] text-[#475569] flex-1 font-mono truncate">{hit.objectType}</span>
                        <span className="text-[10px] font-mono text-[#64748b] truncate max-w-[160px]">{hit.source}</span>
                        <span
                          className="text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0"
                          style={{
                            background: hit.result === 'MATCH' || hit.result === 'CLEAR' || hit.result === 'PASS' ? 'rgba(52,211,153,.15)' : hit.result === 'FAIL' || hit.result === 'NO_MATCH' ? 'rgba(248,113,113,.15)' : 'rgba(148,163,184,.15)',
                            color: hit.result === 'MATCH' || hit.result === 'CLEAR' || hit.result === 'PASS' ? '#15803d' : hit.result === 'FAIL' || hit.result === 'NO_MATCH' ? '#b91c1c' : '#475569',
                          }}
                        >
                          {hit.result}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {duplicateMatched !== null && <DuplicateCheckCard matched={duplicateMatched} />}

              <RawJsonCollapsible data={fullResult} />
            </div>
          )}
        </div>
      </div>
      </>
      )}
      </>
      )}
    </div>
  )
}
